import React from "react";
import isPropValid from "@emotion/is-prop-valid";
import { StyleSheetManager } from "styled-components";

/**
 * The ONE styled-components configuration, shared by the app and by tests.
 *
 * styled-components v6 stopped auto-filtering unknown props on host elements, so
 * a custom prop on `styled.div<{ isActive: boolean }>` reaches the DOM as an
 * invalid attribute unless something filters it. This re-implements v5's
 * filtering: forward a prop to a host (string) element only when it is a real
 * HTML/SVG attribute; always forward to composite components, which do their own
 * filtering (framer-motion) or genuinely want the prop.
 *
 * It lives here rather than inline in `index.tsx` because TESTS need the same
 * configuration. Without it, `renderWithProviders` rendered every component
 * under different rules than production: props the app filters out correctly
 * (`isActive`, `fullWidth`, `isMobileScreen`, `buttonTheme`, `textColor`,
 * `fillColor`, `layoutReversed`, `noPadding`, `backgroundColor`) leaked in jsdom
 * only, so the suite emitted ten "React does not recognize the X prop" warnings
 * for things that were never broken — and a console-based regression guard was
 * impossible, because the noise was indistinguishable from a real leak.
 *
 * This filter is a safety net, not a licence. `@emotion/is-prop-valid` allows a
 * superset of what React DOM accepts (`on`, `option`, `results`, … are React-15
 * legacy names it never pruned), so a prop can pass the filter and still be
 * wrong. Name custom props with a `$` prefix — styled-components never forwards
 * those, whatever they are called.
 */
export const shouldForwardProp = (propName: string, target: unknown) =>
  typeof target === "string" ? isPropValid(propName) : true;

export function StyledConfigProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <StyleSheetManager shouldForwardProp={shouldForwardProp}>
      {children as React.ReactElement}
    </StyleSheetManager>
  );
}

export default StyledConfigProvider;
